import * as PopoverPrimitive from '@radix-ui/react-popover';
import { IconX } from '@tabler/icons-react';
import { format } from 'date-fns/format';
import { ComponentProps, useCallback, useEffect, useState } from 'react';
import { DateRange, Matcher, PropsRange, PropsSingle } from 'react-day-picker';

import { BaseUIComponentProps } from './base';
import { Calendar } from './calendar';
import { DropdownTriggerButton } from './dropdown-trigger-button';
import { InputGroup, InputGroupProps } from './form/inputgroup';
import { useListenBodyScroll } from './hooks/use-listen-body-scroll';
import { DatePeriod, isDatePeriod } from './period-selector';
import { PassModeToChildren } from './provider';

export const getRangeLabel = (range: DateRange | DatePeriod | undefined, formatStr: string = 'dd.MM.yyyy') => {
    if (range) {
        const from = isDatePeriod(range) ? range.start : range.from;
        const to = isDatePeriod(range) ? range.end : range.to;

        if (from && to) {
            return `${format(from, formatStr)} - ${format(to, formatStr)}`;
        } else if (from) {
            return `From ${format(from, formatStr)}`;
        } else if (to) {
            return `To ${format(to, formatStr)}`;
        }
    }

    return '';
};

type DatePickerProps<T extends boolean> = {
    value?: (T extends true ? DateRange : Date) | null | undefined;
    onChange?: (date: (T extends true ? DateRange : Date) | null) => void;

    pickerMode?: 'button'; // TODO: I guess we also want a mode where one can manually input the date ?
    // pickerMode: 'button' | 'input',

    wrapperClassName?: string;

    noClear?: boolean;

    minDate?: Date;
    maxDate?: Date;

    range?: T;

    formatStr?: string;
    disabled?: boolean;

    placeholder?: string;

    isLoading?: boolean;

    noInputGroup?: boolean;

    labelRenderer?: (
        renderDefault: () => string,
        value: T extends true ? DateRange | null | undefined : Date | null | undefined,
        placeholder: string
    ) => string;

    pickerProps?: Omit<ComponentProps<typeof Calendar>, 'selected' | 'onSelect' | 'mode'>;
} & Pick<InputGroupProps, 'label' | 'hasError' | 'errorMessage'> &
    BaseUIComponentProps;

const extractFromMonthForRange = (value: Date | DateRange | null | undefined): Date | undefined => {
    if (value instanceof Date) {
        return value;
    } else if (value && value.from) {
        return value.from;
    }

    return undefined;
};

const defaultLabelRenderer = (
    renderDefault: () => string,
    value: Date | DateRange | null | undefined,
    placeholder: string
) => {
    return renderDefault();
};

export function DatePicker<T extends boolean>({
    value,
    onChange,
    mode,
    className,
    wrapperClassName,
    label,

    hasError,
    errorMessage,

    noClear = false,

    noInputGroup = false,

    isLoading,

    placeholder = 'Select a date',

    minDate,
    maxDate,

    // pickerMode = "button", // TODO: input mode is not implemented yet

    range,

    formatStr = 'dd.MM.yyyy',
    disabled,

    labelRenderer,

    pickerProps,
}: DatePickerProps<T>) {
    type ValueType = T extends true ? DateRange : Date;

    const [internalValue, setInternalValue] = useState<ValueType | null>(value ?? null);

    const labelRenderFunc = labelRenderer ?? defaultLabelRenderer;

    const [isOpen, setIsOpen] = useState(false);

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (disabled) {
                return;
            }

            setIsOpen(open);
        },
        [setIsOpen, disabled]
    );

    const formatFn = (range ? getRangeLabel : format) as T extends true
        ? (range: DateRange, formatStr: string) => string
        : (date: Date, formatStr: string) => string;

    const onChangeHandler = useCallback(
        (date: ValueType | undefined) => {
            setInternalValue(date ?? null);
            onChange?.(date ?? null);

            // Only close the popover if the date is selected (when not in range mode)
            if (!range) {
                setIsOpen(false);
            }
        },
        [onChange, setIsOpen, range]
    );

    const pickerPropsFinal = {
        ...pickerProps,

        defaultMonth:
            pickerProps?.defaultMonth !== undefined ? pickerProps?.defaultMonth : extractFromMonthForRange(value),
    };

    useEffect(() => {
        if (value !== undefined) {
            setInternalValue(value ?? null);
        }
    }, [value]);

    useListenBodyScroll(() => {
        setIsOpen((open) => {
            if (open) {
                return false; // Close the popover when the body is scrolled
            }

            return open; // Otherwise, keep the current state
        });
    }, [setIsOpen]);

    let disabledMatchers: Matcher[] = [];

    if (minDate) {
        disabledMatchers.push({ before: minDate });
    }
    if (maxDate) {
        disabledMatchers.push({ after: maxDate });
    }

    if (pickerPropsFinal?.disabled) {
        disabledMatchers = [...disabledMatchers, ...(pickerPropsFinal.disabled as Matcher[])];
    }

    const extraProps: Pick<T extends true ? PropsRange : PropsSingle, 'mode' | 'selected' | 'onSelect'> = (
        range
            ? {
                  mode: 'range',
                  selected: internalValue ?? undefined,
                  onSelect: (date: DateRange) => onChangeHandler(date as ValueType),
              }
            : {
                  mode: 'single',
                  selected: internalValue ?? undefined,
                  onSelect: (date: Date) => onChangeHandler(date as ValueType),
              }
    ) as Pick<T extends true ? PropsRange : PropsSingle, 'mode' | 'selected' | 'onSelect'>;

    const hasValue = internalValue !== null;

    const node = (
        <PopoverPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
            <PopoverPrimitive.Trigger asChild className="focus:outline-none">
                <DropdownTriggerButton
                    isOpen={isOpen}
                    mode={mode}
                    className={className}
                    disabled={disabled}
                    isLoading={isLoading}
                    hasError={hasError}
                >
                    {labelRenderFunc(
                        () => (internalValue ? formatFn(internalValue as any, formatStr) : placeholder),
                        internalValue,
                        placeholder
                    )}

                    {noClear || !hasValue ? null : (
                        <div className="absolute top-0 bottom-0 right-6 mr-2 flex items-center justify-center">
                            <IconX
                                size={16}
                                className={
                                    'text-token-text-subtitle-defaultNew cursor-pointer group-focus:text-neutral-50 group-hover:text-neutral-50 focus:outline-none focus:text-white'
                                }
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();

                                    onChangeHandler(undefined);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.stopPropagation();
                                        e.preventDefault();

                                        onChangeHandler(undefined);
                                    }
                                }}
                                tabIndex={0}
                            />
                        </div>
                    )}
                </DropdownTriggerButton>
            </PopoverPrimitive.Trigger>
            <PopoverPrimitive.Content className="z-50">
                <PassModeToChildren mode={mode}>
                    <Calendar
                        {...pickerPropsFinal}
                        className="mt-2"
                        required={pickerProps?.required ?? false}
                        {...(extraProps as any)}
                        disabled={disabledMatchers}
                    />
                </PassModeToChildren>
            </PopoverPrimitive.Content>
        </PopoverPrimitive.Root>
    );

    if (noInputGroup) {
        return node;
    }

    return (
        <InputGroup
            label={label}
            hasError={hasError}
            errorMessage={errorMessage}
            mode={mode}
            className={wrapperClassName}
        >
            {node}
        </InputGroup>
    );
}
